import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Stethoscope } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { orgReturnTo, type Specialty } from './org-types';

export function SpecialtyPage() {
  const { specialtyId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), orgReturnTo('specialties'));

  const specialties = useQuery({
    queryKey: ['specialties'],
    queryFn: () => get<Specialty[]>('/org/specialties'),
    enabled: Boolean(specialtyId),
  });
  const specialty = specialties.data?.find((row) => row.id === specialtyId) ?? null;

  if (specialtyId && !specialty) {
    return (
      <FormScreen title={tr('admin', 'loading')} icon={Stethoscope} backTo={returnTo}>
        <RecordState error={specialties.error} missing={Boolean(specialties.data)} />
      </FormScreen>
    );
  }

  return <SpecialtyForm key={specialtyId || 'new'} specialty={specialty} returnTo={returnTo} />;
}

function SpecialtyForm({ specialty, returnTo }: { specialty: Specialty | null; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasModule } = useAuth();
  const [initial] = useState(() => ({
    code: specialty?.code ?? '',
    name: specialty?.name ?? '',
    nphiesPracticeCode: specialty?.nphiesPracticeCode ?? '',
    isActive: specialty?.isActive ?? true,
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code: form.code,
        name: form.name,
        nphiesPracticeCode: form.nphiesPracticeCode.trim() || undefined,
        isActive: form.isActive,
      };
      return specialty ? put(`/org/specialties/${specialty.id}`, payload) : post('/org/specialties', payload);
    },
    onSuccess: () => {
      toast.success(specialty ? tr('admin', 'specialty_updated') : tr('admin', 'specialty_added'));
      void queryClient.invalidateQueries({ queryKey: ['specialties'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.code.length >= 2 && form.name.trim().length >= 2;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={specialty ? tr('admin', 'edit_x', { name: specialty.name }) : tr('admin', 'add_a_specialty')}
      description={tr('admin', 'printed_under_a_doctors_name_a_specialty_is')}
      icon={Stethoscope}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'specialty')} icon={Stethoscope}>
        <Field label={tr('admin', 'name')} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'code')} required hint={tr('admin', 'uppercase_letters_digits_and_underscores')}>
          <Input
            value={form.code}
            maxLength={16}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
          />
        </Field>
        {hasModule('nphies') && (
          <Field label={tr('admin', 'nphies_practice_code')} hint={tr('admin', 'sent_with_claims_for_this_specialty')}>
            <Input value={form.nphiesPracticeCode} onChange={(e) => setForm({ ...form, nphiesPracticeCode: e.target.value })} />
          </Field>
        )}
        <div className="flex items-end pb-2">
          <Checkbox checked={form.isActive} onCheckedChange={(checked) => setForm({ ...form, isActive: checked })} label={tr('admin', 'in_use')} />
        </div>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={specialty ? tr('admin', 'save_changes') : tr('admin', 'add_specialty')}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
